import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import { useSupabase } from '../contexts/SupabaseContext';
import { useBusiness } from '../contexts/BusinessContext';
import { useTodoViewer } from './useTodoViewer';
import {
  findTodoItems,
  isTodoOverdue,
  type TodoItemWithRelations,
} from '../lib/todos';
import type { CrmRequiredActionItem } from '../lib/crm/requiredActions';

/** Due moment as a Date — due_time when set, else end of the due day. */
function dueMoment(item: TodoItemWithRelations): Date {
  const time = item.due_time ? item.due_time.slice(0, 5) : '23:59';
  return new Date(`${item.due_date}T${time}:00`);
}

export function buildDueTaskRequiredActions(
  items: TodoItemWithRelations[],
): CrmRequiredActionItem[] {
  return items
    .filter((item) => !!item.due_date)
    .map((item) => {
      const due = dueMoment(item);
      const overdue = isTodoOverdue(item);
      const dueLabel = overdue
        ? `Due ${formatDistanceToNow(due, { addSuffix: true })}`
        : item.due_time
          ? `Due today at ${format(due, 'h:mm a')}`
          : 'Due today';
      return {
        kind: 'due_task' as const,
        todoId: item.id,
        todoOverdue: overdue,
        title: item.title,
        customerName: item.list?.name || 'Inbox',
        subtitle: dueLabel,
        sortOrder: due.getTime(),
        dueAt: due.toISOString(),
        createdAt: item.created_at,
      };
    })
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

/**
 * Control Centre Tasks that are due today or overdue, scoped to the current
 * viewer's task world — admins see their own, resources see theirs.
 */
export function useDueTasksRequiredActions(enabled = true) {
  const supabase = useSupabase();
  const { selectedBusiness } = useBusiness();
  const viewer = useTodoViewer();
  const businessId = selectedBusiness?.id ?? '';

  const [items, setItems] = useState<TodoItemWithRelations[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDueTasks = useCallback(async () => {
    if (!businessId || !viewer) return;
    const rows = await findTodoItems(businessId, {
      status: 'open',
      actually_due: true,
      visible_to: viewer,
      limit: 50,
    });
    setItems(rows);
  }, [businessId, viewer]);

  const load = useCallback(async () => {
    if (!enabled || !businessId) {
      setItems([]);
      setIsLoading(false);
      return;
    }
    // Permission context still resolving — stay in loading, effect re-runs when it lands.
    if (!viewer) return;
    setError(null);
    try {
      await fetchDueTasks();
    } catch (err) {
      console.error('Error loading due task required actions:', err);
      setError('Failed to load due tasks.');
    } finally {
      setIsLoading(false);
    }
  }, [enabled, businessId, viewer, fetchDueTasks]);

  useEffect(() => {
    void load();
  }, [load]);

  // The Tasks modal / AI dispatch this after any change — cheap instant refresh.
  useEffect(() => {
    if (!enabled) return;
    const onChanged = () => void fetchDueTasks();
    window.addEventListener('todoItemsChanged', onChanged);
    return () => window.removeEventListener('todoItemsChanged', onChanged);
  }, [enabled, fetchDueTasks]);

  const refreshDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!enabled || !businessId || !supabase) return;

    const channel = supabase
      .channel(`due-tasks-required-actions-${businessId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'todo_items',
          filter: `business_id=eq.${businessId}`,
        },
        () => {
          if (refreshDebounceRef.current) clearTimeout(refreshDebounceRef.current);
          refreshDebounceRef.current = setTimeout(() => {
            void fetchDueTasks();
          }, 400);
        },
      )
      .subscribe();

    return () => {
      if (refreshDebounceRef.current) clearTimeout(refreshDebounceRef.current);
      void supabase.removeChannel(channel);
    };
  }, [enabled, businessId, supabase, fetchDueTasks]);

  const dueTaskActions = useMemo(() => buildDueTaskRequiredActions(items), [items]);

  return {
    dueTaskActions,
    isLoading,
    error,
    refresh: load,
  };
}
